import * as React from 'react'
import i18next from 'i18next'
import type { CommonDeps } from '../base/CommonDeps'
import { type LoopPathIds, toLoopPath } from '../../../shared/domain/flows/LoopPath.no-deps'
import {
    getUnitIdsFromLoopPlan,
    type LoopBreadthIds,
    type LoopPlan,
} from '../../../shared/domain/flows/LoopPlan.no-deps'
import type { ElementId, LoopElement, Step } from '../../../shared/domain/flows'
import type { AppUnitType, UnitData } from '../../../shared/domain/units/types.no-deps'
import { type CachedUnitData, getDataForUnit } from '../../../shared/domain/units/CachedUnitData.no-deps'
import type { PromiseState } from '../util/PromiseState'
import type { KnownUnit } from '../unit/unit'
import { LoopSubject } from './LoopSubject'
import type { RenderCachedImage, WithCachedImageUrl } from '../image/WithCachedImage'
import { ElementUnitSelect } from './ElementUnitSelect'
import type { TimelineDetails } from './TimelineDetails'
import { type CreateAnswerBase, isUnanswered } from '../../../shared/domain/flows/answer.no-deps'
import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { BarcodeResult } from '../unit/barcodeTypes'
import { useAsyncEffect } from '../util/react'
import { useState } from 'react'
import type { ParentIsUserSelectable, Unit, UnitFilter } from '../../../shared/domain/flows/UnitFilter.no-deps'
import { getElementFailures, getLatestUnitFilterAnswers } from '../../../shared/domain/flows/summary.no-deps'
import { haveSharedMembers } from '../../../shared/lib/array.no-deps'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { Checkbox } from '../../../shared/webcomponents/react-wrappers/Checkbox'
import type { Maybe } from '@zaptic-external/ts-monads'
import { PopupMessageModal } from '../../../shared/webcomponents/react-wrappers/PopupMessageModal'
import { getValidationMessage } from './validationMessage'
import { findElementInSection } from '../../../shared/domain/flows/element.no-deps'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import type { UnitWorkflowParameter } from '../../../shared/domain/UnitWorkflowParameter/types.no-deps'

interface Props {
    deps: CommonDeps
    element: LoopElement
    renderSteps: (steps: Step[], loopPathIds: LoopPathIds, subjectData: UnitData) => React.ReactNode
    loopPathIds: LoopPathIds
    loopPlan: LoopPlan
    cachedUnitData: CachedUnitData
    allUnitsState: PromiseState<KnownUnit[]>
    withCachedImageUrl: WithCachedImageUrl
    timelineDetails: TimelineDetails
    answers: Flows.Answer[]
    createAnswer: (answer: Flows.Answer) => Promise<void>
    createAnswerBase: CreateAnswerBase
    removeAnswers: (answers: Flows.Answer[]) => Promise<void>
    renderCachedImage: RenderCachedImage
    barcodeScanForUnits?: {
        scan: (units: KnownUnit[]) => Promise<BarcodeResult>
        fetchUnitType: (id: number) => Promise<AppUnitType>
    }
    fetchUnitsByType: (unitTypes: number[]) => Promise<KnownUnit[]>
    fetchUnitType: (unitTypeId: number) => Promise<AppUnitType | null>
    fetchUnitTypeName: (unitTypeId: number) => Promise<Maybe<string>>
    showValidation: boolean
    flowSummary: Flows.FlowSummary
    scheduleParameters: UnitWorkflowParameter[]
}

type UnitSelectData = {
    unitType: AppUnitType
    unitTypeName: string
    unitIds: number[]
    filter: ParentIsUserSelectable
}

export function hasSharedMembersWithAllUnits(unit: Unit, filterByUnitIds: number[]) {
    if (filterByUnitIds.length === 0) return true

    for (const unitId of filterByUnitIds) {
        if (!haveSharedMembers(unit.parents, [unitId])) return false
    }

    return true
}

const uuidToElementId = (uuid: UUID) => uuid as ElementId

function hasUnitSelectFilters(unitFilters: UnitFilter[]) {
    return unitFilters.filter((filter) => filter.type === 'ParentIsUserSelectable').length > 0
}

function loopContainsAnswer(loop: LoopElement, loopPathIds: LoopPathIds, answer: Flows.Answer) {
    return answer.loopPath.startsWith(toLoopPath(loopPathIds)) && isNotNil(findElementInSection(loop, answer.element))
}

export function ElementLoop(props: Props) {
    const unitFilters = props.element.unitFilters.map((filter) => {
        // if there is filter by parameter but there is no parameter value defined for it, then treat is as UserSelectable
        if (
            filter.type === 'ParentFromParameter' &&
            !props.scheduleParameters.find((param) => param.workflowParameter === filter.parameterId)
        ) {
            return { ...filter, id: filter.parameterId as UUID, type: 'ParentIsUserSelectable' as const }
        }
        return filter
    })
    const [units, setUnits] = useState<KnownUnit[]>()
    const [unitSelectData, setUnitSelectData] = useState<UnitSelectData[]>()
    const [unitAnswers, setUnitAnswers] = useState<{ [id: ElementId]: Flows.UnitSelectAnswer }>({})
    const [loopBreadthIds, setLoopBreadthIds] = useState<LoopBreadthIds>(
        hasUnitSelectFilters(unitFilters) ? [] : getUnitIdsFromLoopPlan(props.loopPlan, props.element.id)
    )
    const [pendingFilterAnswer, setPendingFilterAnswer] = useState<Flows.Answer>()

    const elementAnswers = props.answers.filter(
        (answer) => loopContainsAnswer(props.element, props.loopPathIds, answer) && !isUnanswered(answer)
    )

    // we only care about these types of filters for the selects
    const elementUnitFilters = unitFilters.filter(
        (filter): filter is ParentIsUserSelectable => filter.type === 'ParentIsUserSelectable'
    )

    const onFilter = async (answer: Flows.Answer) => {
        return elementAnswers.length ? setPendingFilterAnswer(answer) : props.createAnswer(answer)
    }

    useAsyncEffect(async () => {
        // we have no filters that need to be selected by the user so just render the loop element with all of
        // the units that we calculated when creating the LoopPlan
        if (elementUnitFilters.length === 0) {
            return
        }

        // need to load the units first as this is what drives the select drop downs
        if (props.allUnitsState.status !== 'resolved') {
            return
        }

        const filtersUnitTypes = await Promise.all(
            elementUnitFilters.map((filter: ParentIsUserSelectable) => filter.unitType).map(props.fetchUnitType)
        )

        // make sure the unit actually exists
        const foundUnitTypes: AppUnitType[] = filtersUnitTypes.filter((type) => isNotNil(type))

        // grab all the units we want to display in the selects
        const unitsForTypes: KnownUnit[][] = await Promise.all(
            foundUnitTypes.map((type) => props.fetchUnitsByType([type.id]))
        )

        const allUnits = [...props.allUnitsState.value, ...unitsForTypes.flat()]

        // add the units we are showing in the select to the list of all units that we need to pass to the
        // <ElementUnitSelect> because that does a look up based on the id
        setUnits(allUnits)

        // build the model that we will use to render the unit selects
        const selectData: UnitSelectData[] = await Promise.all(
            foundUnitTypes.map(async (unitType, index) => {
                return {
                    unitTypeId: unitType.id,
                    unitType,
                    unitTypeName: (await props.fetchUnitTypeName(unitType.id)).withDefault(unitType.name),
                    unitIds: unitsForTypes[index].map((item) => item.id),
                    filter: elementUnitFilters.find(
                        (filter: ParentIsUserSelectable) => filter.unitType === unitType.id
                    ),
                }
            })
        )

        setUnitSelectData(selectData)
        const userSelectUnits = selectData

        if (userSelectUnits?.length) {
            const latestUnitFilterAnswers = getLatestUnitFilterAnswers(elementUnitFilters, props.answers)

            const selectAnswers: { [id: ElementId]: Flows.UnitSelectAnswer } = latestUnitFilterAnswers.reduce(
                (obj, answer) => Object.assign(obj, { [answer.element]: answer }),
                {}
            )
            setUnitAnswers(selectAnswers)

            // grab all of the unit ids from the answers, this will be used later to filter the elements
            const completedUnitSelectAnswerIds: number[] = latestUnitFilterAnswers
                .map((answer) => answer.value)
                .flat()
                .filter((answer): answer is number => typeof answer === 'number')

            const disableFilterAnswerCount = latestUnitFilterAnswers.filter(
                (answer) => answer.value && (answer.value as number[]).length === 0
            ).length

            // get all of the possible units this loop element will show, this has already been determined
            // when creating the loop plan
            // given what the user has chosen the unit selects we filter the list down to what is valid
            const validUnits: KnownUnit[] = getUnitIdsFromLoopPlan(props.loopPlan, props.element.id)
                .map((loopId) => allUnits.find((unit) => unit.id === loopId.unitId))
                .filter((unit) => unit && hasSharedMembersWithAllUnits(unit, completedUnitSelectAnswerIds))

            // make sure the user has selected everything they need to before we show any loop elements
            const hasAllSelectAnswers =
                completedUnitSelectAnswerIds.length + disableFilterAnswerCount === userSelectUnits.length

            // this is what controls what elements will be shown in the loop
            setLoopBreadthIds(hasAllSelectAnswers ? validUnits.map((unit) => ({ unitId: unit.id })) : [])
        }
    }, [props.allUnitsState, props.element, props.answers, props.loopPlan])

    const createAnswer = (fn: (base: Flows.AnswerBase) => Flows.UnitSelectAnswer, unitSelect: UnitSelectData) => {
        const answer = fn(
            props.createAnswerBase(
                uuidToElementId(unitSelect.filter.id),
                props.loopPathIds,
                unitAnswers[uuidToElementId(unitSelect.filter.id)],
                null,
                true
            )
        )
        return onFilter(answer)
    }

    const findUnitSelectAnswer = (unitSelectElementId: UUID): Flows.UnitSelectAnswer | null => {
        const elementId = uuidToElementId(unitSelectElementId)
        return unitAnswers[elementId] ? unitAnswers[elementId] : null
    }

    const findUnitSelectAnswerValue = (unitSelectElementId: UUID): Flows.SelectedUnits | null => {
        const elementId = uuidToElementId(unitSelectElementId)
        return unitAnswers[elementId] ? (unitAnswers[elementId].value as Flows.SelectedUnits) : null
    }

    const isUnitFilterDisabled = (unitSelectElementId: UUID) => {
        const answer = findUnitSelectAnswerValue(unitSelectElementId)
        // if answer is null then it means it's not selected yet, it doesn't mean it's disabled
        // if answer is an empty array then it's disabled, because unit select answer is either null or non empty array
        return answer && answer.length === 0
    }

    return (
        <>
            {unitSelectData && (
                <>
                    {unitSelectData.map((unitSelect) => {
                        /**
                         * Most elements is getting already prepared validationMessages as prop
                         * but in content loop we have kind of "nested unit select elements" that are used within ContentLoop
                         * and we need to display validation messages for these too. We cannot just pass validationMessage array because then
                         * we woudln't know which message is for which "nested unit select", hence we generate validation messages here
                         * from flowSummary that is passed from parent element.
                         */
                        const validationMessages: string[] = props.showValidation
                            ? getElementFailures(
                                  props.flowSummary,
                                  unitSelect.filter.id as ElementId,
                                  props.loopPathIds
                              ).map((failure) =>
                                  getValidationMessage(
                                      {
                                          type: 'unit-select',
                                          id: unitSelect.filter.id as ElementId,
                                          label: i18next.t('appcore:ElementLoop.unitToFilterBySelectLabel', {
                                              unitTypeName: unitSelect.unitTypeName,
                                          }),
                                          unitTypes: [unitSelect.unitType.id],
                                      },
                                      failure.reason
                                  )
                              )
                            : []
                        const dontFilterUnitsCheckbox = (
                            <Checkbox
                                className="AppCore__flow__ElementLoop_Checkbox"
                                selected={isUnitFilterDisabled(unitSelect.filter.id)}
                                label={i18next.t('appcore:ElementLoop.disableUnitsFilter')}
                                controlled={true}
                                onClick={() => {
                                    const answer: Flows.UnitSelectAnswer = {
                                        ...props.createAnswerBase(
                                            uuidToElementId(unitSelect.filter.id),
                                            props.loopPathIds,
                                            findUnitSelectAnswer(unitSelect.filter.id),
                                            null,
                                            true
                                        ),
                                        type: 'unit-select',
                                        value: isUnitFilterDisabled(unitSelect.filter.id) ? null : [],
                                    }
                                    onFilter(answer)
                                }}
                            />
                        )
                        return (
                            <div key={unitSelect.unitType.id}>
                                <ElementUnitSelect
                                    deps={props.deps}
                                    elementId={props.element.id}
                                    loopPathIds={props.loopPathIds}
                                    label={i18next.t('appcore:ElementLoop.unitToFilterBySelectLabel', {
                                        unitTypeName: unitSelect.unitTypeName,
                                    })}
                                    timelineDetails={props.timelineDetails}
                                    required={true}
                                    unitIds={unitSelect.unitIds}
                                    unitTypes={[unitSelect.unitType.id]}
                                    allUnits={units}
                                    maxUnits={1}
                                    validationMessages={validationMessages}
                                    createAnswer={(fn) => createAnswer(fn, unitSelect)}
                                    renderCachedImage={props.renderCachedImage}
                                    answerValue={findUnitSelectAnswerValue(unitSelect.filter.id) as Flows.SelectedUnits}
                                    barcodeScanForUnits={props.barcodeScanForUnits}
                                    disabled={isUnitFilterDisabled(unitSelect.filter.id)}
                                    dontFilterUnitsCheckbox={dontFilterUnitsCheckbox}
                                />
                            </div>
                        )
                    })}
                </>
            )}
            {loopBreadthIds.length > 0 && (
                <div className="AppCore__flow__ElementLoop">
                    {loopBreadthIds.map((loopId) => {
                        const newLoopPathIds = [...props.loopPathIds, loopId]
                        const newSubjectData = getDataForUnit(props.cachedUnitData, loopId.unitId)

                        return (
                            <div className="AppCore__flow__ElementLoop__iteration" key={toLoopPath(newLoopPathIds)}>
                                <LoopSubject
                                    deps={props.deps}
                                    allUnitsState={props.allUnitsState}
                                    withCachedImageUrl={props.withCachedImageUrl}
                                    loopPathIds={newLoopPathIds}
                                />
                                {props.renderSteps(props.element.steps, newLoopPathIds, newSubjectData)}
                            </div>
                        )
                    })}
                </div>
            )}
            <PopupMessageModal
                isOpen={Boolean(pendingFilterAnswer)}
                hasCustomOverlay={true}
                isDangerous={true}
                heading={i18next.t('appcore:ElementLoop.confirmChangeUnitFilterHeading')}
                message={i18next.t('appcore:ElementLoop.confirmChangeUnitFilterMessage')}
                submitLabel={i18next.t('appcore:ElementLoop.confirmChangeUnitFilter')}
                submitTestId={`submit-parent-change-for-loop-${props.element.id}`}
                onCancel={() => setPendingFilterAnswer(undefined)}
                onSubmit={() => {
                    props.createAnswer(pendingFilterAnswer)
                    props.removeAnswers(elementAnswers)
                    setPendingFilterAnswer(undefined)
                }}
            />
        </>
    )
}
